import { useEffect, useRef, useState } from 'react';
import type { Notification } from '@schwarz/contract';

import {
  NOTIFICATION_POLL_MS,
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
} from '../../store/dashboard-api.js';
import { IconAlert, IconBell, IconCheck, IconHeart, IconTrend } from '../../ui/icons.js';
import { when } from '../../ui/when.js';
import styles from './NotificationBell.module.css';

/**
 * Notifications (S12).
 *
 * ## Why this polls when the rest of the console does not
 *
 * Every other live view here polls only while something could change unattended.
 * The bell is the opposite case by definition: its whole purpose is to tell an
 * operator about something they were not looking for, so it polls whenever it is
 * mounted — which is the whole session, since it lives in the top bar. Fifteen
 * seconds, per Q8, and the endpoint contract is unchanged if this becomes an
 * event stream later.
 *
 * ## Why read state is a request and not local state
 *
 * The mock tracks it in component memory, which loses everything on reload and
 * means two tabs disagree. It is per user and server-side here, so an alert
 * somebody dealt with on Monday is still dealt with on Tuesday.
 */
export function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data } = useGetNotificationsQuery(undefined, {
    pollingInterval: NOTIFICATION_POLL_MS,
    // Kept polling while the tab is in the background: an alert that only
    // arrives once somebody clicks back into the window is an alert that waited.
    skipPollingIfUnfocused: false,
  });
  const [markRead] = useMarkNotificationsReadMutation();

  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  // Close on a click outside or on Escape — the two things everybody tries on a
  // panel that has appeared over the page.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent): void => {
      if (wrapRef.current !== null && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  /**
   * Clicking an alert marks it read, and nothing else.
   *
   * It used to navigate as well, to wherever the alert pointed. That turns out to
   * be the wrong trade: the panel is read as a list, and a click that both
   * dismisses an item and throws the reader onto another page costs them their
   * place in the list they were working down. Reading is the common action;
   * following up is a decision to take afterwards, from the page they choose.
   *
   * The alert still carries its `linkType` and `linkId`, so the panel can offer
   * an explicit way through later without this having to be undone.
   */
  const dismiss = (item: Notification): void => {
    if (!item.read) void markRead({ ids: [item.id] });
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.button} ${open ? styles.active : ''}`}
        aria-label={unread === 0 ? 'Notifications' : `Notifications, ${String(unread)} unread`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <IconBell />
        {unread > 0 && <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.head}>
            <div>
              <strong>Notifications</strong>
              <span className={styles.headSub}>
                {unread > 0 ? `${String(unread)} unread` : 'All caught up'}
              </span>
            </div>
            {unread > 0 && (
              <button type="button" className={styles.markAll} onClick={() => void markRead({})}>
                Mark all read
              </button>
            )}
          </div>

          <div className={styles.list}>
            {items.length === 0 && (
              <div className={styles.empty}>
                Nothing yet. Hot leads, removal requests and budget warnings appear here.
              </div>
            )}

            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`${styles.item} ${item.read ? '' : styles.unread}`}
                onClick={() => dismiss(item)}
              >
                <span className={`${styles.icon} ${styles[item.severity.toLowerCase()] ?? ''}`}>
                  <Glyph item={item} />
                </span>
                <span className={styles.body}>
                  <span className={styles.title}>
                    {item.title}
                    {!item.read && <span className={styles.dot} aria-hidden="true" />}
                  </span>
                  {item.body !== null && <span className={styles.text}>{item.body}</span>}
                  <span className={styles.time}>{when(item.createdAt)}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The glyph for one alert.
 *
 * A lead alert says which of the two it is — a heart for somebody worth calling
 * back, a warning for somebody who asked not to be. Everything else falls back to
 * its severity, which is what the mock does.
 */
function Glyph({ item }: { item: Notification }): JSX.Element {
  if (item.kind === 'LEAD') {
    return item.severity === 'WARNING' || item.severity === 'CRITICAL' ? (
      <IconAlert width="13" height="13" />
    ) : (
      <IconHeart width="13" height="13" />
    );
  }

  if (item.severity === 'WARNING' || item.severity === 'CRITICAL') {
    return <IconAlert width="13" height="13" />;
  }
  if (item.severity === 'OK') return <IconCheck width="13" height="13" />;

  return <IconTrend width="13" height="13" />;
}
